import AxiosUtils from 'utils/axios-utils';
import { SERVER_FILE_ENDPOINT } from 'constants/envs';
import {
  extractErrorMessage,
  parseFilename,
  triggerDownload,
} from 'pages/attendance-manager/component/export-all-modal';
import type { HandoverInfo, QuoteData, QuoteSettings, QuoteStatus } from './calc';

/** Báo giá thiết bị — backend src/equipment-quotes (docs/bao-gia-thiet-bi/01-dac-ta-2026-10-27.md §5). */
const BASE = '/equipment-quotes';

export interface EquipmentItem {
  id: number;
  category: string;
  name: string;
  brand: string | null;
  price: number;
  unit: string;
  note: string | null;
  /** Key ảnh trên Spaces (hp/<uuid>) hoặc null. */
  imageKey: string | null;
  isActive: boolean;
  /** Tặng kèm mọi gói: báo giá mới tự thêm; Creator HP Media miễn phí. */
  isIncluded: boolean;
  /** NONE = món thường · FREE = tặng kèm miễn phí · ADVANCE = có sẵn mọi gói, Công ty ứng trước, Creator trả lại. */
  includeMode: 'NONE' | 'FREE' | 'ADVANCE';
  sortOrder: number;
  updatedByName: string | null;
}

export interface QuoteView {
  id: number;
  code: string;
  customerName: string;
  customerPhone: string | null;
  tiktokId: string | null;
  status: QuoteStatus;
  totalAmount: number;
  companyAmount: number;
  creatorAmount: number;
  createdByName: string | null;
  updatedByName: string | null;
  createdAt: string;
  updatedAt: string;
  data?: QuoteData;
  handoverAt?: string | null;
  paperReceived?: boolean;
  contractNo?: string | null;
}

export interface PermissionRow {
  id: number;
  nickName: string | null;
  username: string | null;
  email: string | null;
  granted: boolean;
  isTechnician: boolean;
}

const data = <T>(p: Promise<any>): Promise<T> => p.then((res) => res?.data as T);

export const equipmentApi = {
  settings: () => data<QuoteSettings>(AxiosUtils.get(`${BASE}/settings`)),
  saveSettings: (s: QuoteSettings) => data<QuoteSettings>(AxiosUtils.put(`${BASE}/settings`, s)),
  permissions: () => data<PermissionRow[]>(AxiosUtils.get(`${BASE}/permissions`)),
  setPermission: (userId: number, granted: boolean) =>
    AxiosUtils.put(`${BASE}/permissions/${userId}`, { granted }),
  items: (all = false) =>
    data<EquipmentItem[]>(AxiosUtils.get(`${BASE}/items`, all ? { all: '1' } : undefined)),
  createItem: (body: Partial<EquipmentItem>) =>
    data<EquipmentItem>(AxiosUtils.post(`${BASE}/items`, body)),
  updateItem: (id: number, body: Partial<EquipmentItem>) =>
    data<EquipmentItem>(AxiosUtils.put(`${BASE}/items/${id}`, body)),
  removeItem: (id: number) => AxiosUtils._delete(`${BASE}/items/${id}`, undefined),
  quotes: (params?: { q?: string; status?: string }) =>
    data<QuoteView[]>(AxiosUtils.get(`${BASE}/quotes`, params)),
  quote: (id: number) =>
    data<{ quote: QuoteView; settings: QuoteSettings }>(AxiosUtils.get(`${BASE}/quotes/${id}`)),
  createQuote: (d: QuoteData) =>
    data<{ quote: QuoteView; settings: QuoteSettings }>(
      AxiosUtils.post(`${BASE}/quotes`, { data: d })
    ),
  updateQuote: (id: number, d: QuoteData) =>
    data<{ quote: QuoteView; settings: QuoteSettings }>(
      AxiosUtils.put(`${BASE}/quotes/${id}`, { data: d })
    ),
  saveHandover: (id: number, h: HandoverInfo) =>
    data<{ quote: QuoteView; settings: QuoteSettings }>(
      AxiosUtils.put(`${BASE}/quotes/${id}/handover`, h)
    ),
  setStatus: (id: number, status: QuoteStatus) =>
    data<{ quote: QuoteView; settings: QuoteSettings }>(
      AxiosUtils.post(`${BASE}/quotes/${id}/status`, { status })
    ),
  duplicate: (id: number) =>
    data<{ quote: QuoteView }>(AxiosUtils.post(`${BASE}/quotes/${id}/duplicate`)),
  /** PNG thu nhỏ qua server (không vướng CORS khi vẽ canvas). Lỗi → null. */
  async thumbnail(key: string): Promise<Blob | null> {
    try {
      const res: any = await AxiosUtils.get(`${BASE}/image`, { key }, undefined, {
        responseType: 'blob',
        timeout: 15_000,
      });
      return res?.data instanceof Blob ? res.data : null;
    } catch {
      return null;
    }
  },
  removeQuote: (id: number) => AxiosUtils._delete(`${BASE}/quotes/${id}`, undefined),
  async exportExcel(id: number, code: string) {
    const response: any = await AxiosUtils.get(
      `${BASE}/quotes/${id}/export`,
      undefined,
      undefined,
      {
        responseType: 'blob',
        timeout: 60_000,
      }
    );
    const filename = parseFilename(
      response?.headers?.['content-disposition'],
      `bao-gia-${code}.xlsx`
    );
    triggerDownload(
      new Blob([response.data], { type: response?.headers?.['content-type'] }),
      filename
    );
  },
};

export const errorText = (err: any): string =>
  err?.message ||
  err?.error ||
  err?.response?.data?.message ||
  'Không thực hiện được. Thử lại sau.';

export { extractErrorMessage, triggerDownload };

export const STATUS_LABEL: Record<QuoteStatus, string> = {
  DRAFT: 'Nháp',
  SENT: 'Đã gửi khách',
  SIGNED: 'Đã ký hợp đồng',
  CANCELLED: 'Huỷ',
};
export const STATUS_COLOR: Record<QuoteStatus, string> = {
  DRAFT: 'default',
  SENT: 'blue',
  SIGNED: 'green',
  CANCELLED: 'red',
};

/** URL hiển thị ảnh sản phẩm (CDN / dev: localhost:3001/static). */
export const imageUrl = (key?: string | null) => (key ? `${SERVER_FILE_ENDPOINT}/${key}` : '');
